/**
 * Board-mode selection — which top-level renderer is active for the
 * in-match Table. The mode is persisted in localStorage so a player's
 * preference survives navigation; the renderers all consume the same
 * `PublicTableState` so switching is instant and state-preserving.
 *
 * Modes:
 *   - classic: original 7-realm grid + side panel (Phase 1 default)
 *   - fullscreen2d: top-bar / middle / bottom-bar / R1–R7 rail layout
 *     mirroring the competing wondersccg.online client
 *   - carousel: mobile-first single-realm-in-focus with ghosted stack
 *     of lower-tier realms on the left and higher-tier on the right
 *   - overhead3d: react-three-fiber tabletop view (follow-up)
 */

import { useCallback, useEffect, useState } from 'react';

export type BoardMode = 'classic' | 'fullscreen2d' | 'carousel' | 'overhead3d' | 'broadcast';

export const BOARD_MODES: readonly { id: BoardMode; label: string; hint: string }[] = [
  { id: 'classic', label: 'Classic', hint: 'Original 7-realm grid layout' },
  { id: 'fullscreen2d', label: 'Fullscreen', hint: 'Top + bottom bar with R1–R7 rail' },
  { id: 'carousel', label: 'Carousel', hint: 'One realm in focus; swipe between tiers' },
  { id: 'overhead3d', label: '3D Tabletop', hint: 'Top-down 3D table' },
  { id: 'broadcast', label: 'Broadcast', hint: 'OBS-friendly read-only spectator HUD' },
] as const;

const STORAGE_KEY = 'wonders-board-mode';
const SAME_TAB_EVENT = 'wonders:board-mode-change';

const isBoardMode = (v: unknown): v is BoardMode =>
  v === 'classic' || v === 'fullscreen2d' || v === 'carousel' || v === 'overhead3d';

/**
 * Multiple components call useBoardMode() (the switcher AND the Table
 * dispatcher) — each with its own useState. Without explicit fan-out
 * the switcher's update only mutates its own state, leaving the Table
 * stuck on whatever mode it initialised with. This hook subscribes to
 * a custom 'wonders:board-mode-change' event dispatched on window, so
 * any setMode call propagates to every other hook instance in the
 * same tab. The native `storage` event is for cross-tab only.
 */
export function useBoardMode(): {
  mode: BoardMode;
  setMode: (m: BoardMode) => void;
} {
  const [mode, setModeState] = useState<BoardMode>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      // Classic is the playable default: clean drag-drop layout with
      // realm lanes, side panel, and persistent zone stacks. The other
      // modes (Fullscreen2D, Carousel, Overhead3D, Broadcast) are
      // alternate layouts opt-in via the BoardModeSwitcher.
      return isBoardMode(v) ? v : 'classic';
    } catch {
      return 'classic';
    }
  });

  const setMode = useCallback((m: BoardMode) => {
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      // localStorage disabled — mode resets next mount, fine.
    }
    // Fan out to other hook instances in this tab.
    window.dispatchEvent(new CustomEvent<BoardMode>(SAME_TAB_EVENT, { detail: m }));
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && isBoardMode(e.newValue)) {
        setModeState(e.newValue);
      }
    };
    const onSameTab = (e: Event) => {
      const next = (e as CustomEvent<BoardMode>).detail;
      if (isBoardMode(next)) setModeState(next);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SAME_TAB_EVENT, onSameTab);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SAME_TAB_EVENT, onSameTab);
    };
  }, []);

  return { mode, setMode };
}
